import React, { useState } from 'react';
import { Button, Input, Select, TextArea } from '@/components/ui';
import { Edit2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import type { UserProfileDetail } from '../types/userDetail.types';
import { UserDetailService } from '../services/userDetail.service';
import { cn } from '@/utils/cn';
import { AuthMask } from '@/core/permissions/AuthMask';

interface GeneralInfoTabProps {
 user: UserProfileDetail;
}

export function GeneralInfoTab({ user }: GeneralInfoTabProps) {
 const [editingBlock, setEditingBlock] = useState<string | null>(null);
 const [tempData, setTempData] = useState<any>(null);
 const [isSaving, setIsSaving] = useState(false);

 const handleEdit = (blockId: string) => {
 setEditingBlock(blockId);
 setTempData({ ...user });
 };

 const handleCancel = () => {
 setEditingBlock(null);
 setTempData(null);
 };

 const handleSave = async () => {
 if (!tempData || !user.id) return;
 setIsSaving(true);
 try {
 await UserDetailService.updateUserDetail(user.id, tempData);
 toast.success('Cập nhật hồ sơ thành công');
 setEditingBlock(null);
 setTempData(null);
 } catch (err: any) {
 toast.error(err.message || 'Lỗi khi cập nhật hồ sơ');
 } finally {
 setIsSaving(false);
 }
 };

 const updateField = (field: string, value: any) => {
 setTempData((prev: any) => ({ ...prev, [field]: value }));
 };

 return (
 <div className="space-y-6">
 {/* Block: Thông tin chung */}
 <div className={cn("bg-white rounded-md border border-slate-100 relative transition-all duration-300",
 editingBlock === 'general' ?"ring-2 ring-emerald-500/10 border-emerald-200" :"group"
 )}>
 <div className="p-6 border-b border-slate-50 flex items-center justify-between">
 <div className="flex items-center gap-3">
 <h3 className="sm:text-sm text-xs text-slate-800">Thông tin chung</h3>
 {editingBlock === 'general' && (
 <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-600 sm:text-[10px] text-[9px] animate-pulse">
 Đang chỉnh sửa
 </span>
 )}
 </div>
 
 <div className="flex items-center gap-2">
 {editingBlock === 'general' ? (
 <>
 <Button 
 variant="outline" 
 size="sm" 
 className="h-8 rounded-md gap-1.5 sm:text-xs text-[10px] border-slate-100 text-slate-500"
 onClick={handleCancel}
 disabled={isSaving}
 >
 <X className="w-3.5 h-3.5" />
 <span>Hủy</span>
 </Button>
 <Button 
 variant="primary" 
 size="sm" 
 className="h-8 rounded-md gap-1.5 sm:text-xs text-[10px]"
 onClick={handleSave}
 disabled={isSaving}
 >
 {isSaving ? <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Check className="w-3.5 h-3.5" />}
 <span>Lưu thay đổi</span>
 </Button>
 </>
 ) : (
 <Button 
 variant="outline" 
 size="sm" 
 className="h-8 rounded-md gap-1.5 sm:text-xs text-[10px] border-slate-100 opacity-0 group-hover:opacity-100 transition-opacity"
 onClick={() => handleEdit('general')}
 >
 <Edit2 className="w-3.5 h-3.5" />
 <span>Chỉnh sửa</span>
 </Button>
 )}
 </div>
 </div>

 <div className="p-6">
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-6">
 <EditableItem 
 isEditing={editingBlock === 'general'} 
 label="Họ và tên" 
 value={editingBlock === 'general' ? tempData?.full_name : user.full_name}
 onChange={(v) => updateField('full_name', v)}
 />
 <EditableItem 
 isEditing={editingBlock === 'general'} 
 label="Mã nhân sự" 
 value={editingBlock === 'general' ? tempData?.user_code : user.user_code}
 onChange={(v) => updateField('user_code', v)}
 />
 <EditableItem 
 isEditing={editingBlock === 'general'} 
 label="Chức vụ" 
 value={editingBlock === 'general' ? tempData?.position_name : user.position_name}
 onChange={(v) => updateField('position_name', v)}
 />
 <EditableItem 
 isEditing={editingBlock === 'general'} 
 label="Phòng ban" 
 value={editingBlock === 'general' ? tempData?.department_name : user.department_name}
 onChange={(v) => updateField('department_name', v)}
 />
 <EditableItem 
 isEditing={editingBlock === 'general'} 
 label="Đội nhóm" 
 value="-"
 onChange={(v) => {}}
 />
 <EditableItem 
 isEditing={editingBlock === 'general'} 
 label="Quản lý trực tiếp" 
 value={editingBlock === 'general' ? tempData?.direct_manager : user.direct_manager}
 onChange={(v) => updateField('direct_manager', v)}
 />
 <EditableItem 
 isEditing={editingBlock === 'general'} 
 label="Địa chỉ thường trú" 
 value={editingBlock === 'general' ? tempData?.permanent_address : user.permanent_address}
 onChange={(v) => updateField('permanent_address', v)}
 className="md:col-span-2"
 />
 <EditableItem 
 isEditing={editingBlock === 'general'} 
 label="Địa chỉ hiện tại" 
 value={editingBlock === 'general' ? tempData?.current_address : user.current_address}
 onChange={(v) => updateField('current_address', v)}
 className="md:col-span-2"
 />
 
 <div className="lg:col-span-3 h-px bg-slate-50 my-2" />

 <EditableItem 
 isEditing={editingBlock === 'general'} 
 label="Ngày sinh" 
 value={editingBlock === 'general' ? tempData?.birthday : user.birthday}
 onChange={(v) => updateField('birthday', v)}
 type="date"
 />
 <EditableItem 
 isEditing={editingBlock === 'general'} 
 label="Giới tính" 
 value={editingBlock === 'general' ? tempData?.gender : user.gender}
 onChange={(v) => updateField('gender', v)}
 type="select"
 options={[
 { value: 'male', label: 'Nam' },
 { value: 'female', label: 'Nữ' },
 { value: 'other', label: 'Khác' }
 ]}
 />
 <EditableItem 
 isEditing={editingBlock === 'general'} 
 label="CCCD/CMND" 
 value={editingBlock === 'general' ? tempData?.id_number : user.id_number}
 onChange={(v) => updateField('id_number', v)}
 isMasked
 />
 <EditableItem 
 isEditing={editingBlock === 'general'} 
 label="Ngày cấp" 
 value={editingBlock === 'general' ? tempData?.id_issue_date : user.id_issue_date}
 onChange={(v) => updateField('id_issue_date', v)}
 type="date"
 />
 <EditableItem 
 isEditing={editingBlock === 'general'} 
 label="Nơi cấp" 
 value={editingBlock === 'general' ? tempData?.id_issue_place : user.id_issue_place}
 onChange={(v) => updateField('id_issue_place', v)}
 />
 <EditableItem 
 isEditing={editingBlock === 'general'} 
 label="Quốc tịch" 
 value={editingBlock === 'general' ? tempData?.nationality : user.nationality}
 onChange={(v) => updateField('nationality', v)}
 />
 <EditableItem 
 isEditing={editingBlock === 'general'} 
 label="Số điện thoại" 
 value={editingBlock === 'general' ? tempData?.phone : user.phone}
 onChange={(v) => updateField('phone', v)}
 isMasked
 />
 <EditableItem 
 isEditing={editingBlock === 'general'} 
 label="Email" 
 value={editingBlock === 'general' ? tempData?.email : user.email}
 onChange={(v) => updateField('email', v)}
 />
 </div>
 </div>
 </div>

 {/* Block: Trình độ & kỹ năng */}
 <div className={cn("bg-white rounded-md border border-slate-100 relative transition-all duration-300",
 editingBlock === 'skills' ?"ring-2 ring-emerald-500/10 border-emerald-200" :"group"
 )}>
 <div className="p-6 border-b border-slate-50 flex items-center justify-between">
 <div className="flex items-center gap-3">
 <h3 className="sm:text-sm text-xs text-slate-800">Trình độ & kỹ năng</h3>
 {editingBlock === 'skills' && (
 <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-600 sm:text-[10px] text-[9px] animate-pulse">
 Đang chỉnh sửa
 </span>
 )}
 </div>
 <div className="flex items-center gap-2">
 {editingBlock === 'skills' ? (
 <>
 <Button variant="outline" size="sm" className="h-8 rounded-md sm:text-xs text-[10px] border-slate-100" onClick={handleCancel}>Hủy</Button>
 <Button variant="primary" size="sm" className="h-8 rounded-md sm:text-xs text-[10px]" onClick={handleSave}>Lưu</Button>
 </>
 ) : (
 <Button 
 variant="outline" 
 size="sm" 
 className="h-8 rounded-md gap-1.5 sm:text-xs text-[10px] border-slate-100 opacity-0 group-hover:opacity-100 transition-opacity"
 onClick={() => handleEdit('skills')}
 >
 <Edit2 className="w-3.5 h-3.5" />
 <span>Chỉnh sửa</span>
 </Button>
 )}
 </div>
 </div>

 <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-12">
 <div className="space-y-4">
 <EditableItem 
 isEditing={editingBlock === 'skills'} 
 label="Trình độ học vấn" 
 value={editingBlock === 'skills' ? tempData?.education_level : user.education_level}
 onChange={(v) => updateField('education_level', v)}
 />
 <EditableItem 
 isEditing={editingBlock === 'skills'} 
 label="Đại học" 
 value={editingBlock === 'skills' ? tempData?.university : user.university}
 onChange={(v) => updateField('university', v)}
 />
 <EditableItem 
 isEditing={editingBlock === 'skills'} 
 label="Chuyên ngành" 
 value={editingBlock === 'skills' ? tempData?.major : user.major}
 onChange={(v) => updateField('major', v)}
 />
 <EditableItem 
 isEditing={editingBlock === 'skills'} 
 label="Năm tốt nghiệp" 
 value={editingBlock === 'skills' ? tempData?.graduation_year : user.graduation_year?.toString()}
 onChange={(v) => updateField('graduation_year', v)}
 />
 </div>
 
 <div className="space-y-4">
 <p className="sm:text-sm text-xs text-slate-400 tracking-wider mb-2">Kỹ năng chuyên môn</p>
 <div className="flex flex-wrap gap-2">
 {user.skills?.map((skill, index) => (
 <span 
 key={index}
 className="px-3 py-1 rounded-md bg-slate-50 text-slate-600 sm:text-sm text-xs border border-slate-100"
 >
 {skill}
 </span>
 ))}
 </div>
 </div>
 </div>
 </div>

 {/* Block: Ghi chú */}
 <div className={cn("bg-white rounded-md border border-slate-100 relative transition-all duration-300",
 editingBlock === 'notes' ?"ring-2 ring-emerald-500/10 border-emerald-200" :"group"
 )}>
 <div className="p-6 border-b border-slate-50 flex items-center justify-between">
 <h3 className="sm:text-sm text-xs text-slate-800">Ghi chú</h3>
 <div className="flex items-center gap-2">
 {editingBlock === 'notes' ? (
 <>
 <Button variant="outline" size="sm" className="h-8 rounded-md sm:text-xs text-[10px] border-slate-100" onClick={handleCancel}>Hủy</Button>
 <Button variant="primary" size="sm" className="h-8 rounded-md sm:text-xs text-[10px]" onClick={handleSave}>Lưu</Button>
 </>
 ) : (
 <Button 
 variant="outline" 
 size="sm" 
 className="h-8 rounded-md gap-1.5 sm:text-xs text-[10px] border-slate-100 opacity-0 group-hover:opacity-100 transition-opacity"
 onClick={() => handleEdit('notes')}
 >
 <Edit2 className="w-3.5 h-3.5" />
 <span>Chỉnh sửa</span>
 </Button>
 )}
 </div>
 </div>
 <div className="p-6">
 {editingBlock === 'notes' ? (
 <TextArea 
 value={tempData?.notes} 
 onChange={(e) => updateField('notes', e.target.value)}
 className="w-full sm:text-sm text-xs"
 rows={4}
 />
 ) : (
 <p className="sm:text-sm text-xs text-slate-500 italic">
 {user.notes ||"Không có ghi chú nào."}
 </p>
 )}
 </div>
 </div>
 </div>
 );
}

interface EditableItemProps {
 isEditing: boolean;
 label: string;
 value: string | undefined;
 onChange: (value: string) => void;
 className?: string;
 type?: 'text' | 'date' | 'select';
 options?: { value: string; label: string }[];
 isMasked?: boolean;
}

function EditableItem({ isEditing, label, value, onChange, className, type = 'text', options, isMasked }: EditableItemProps) {
 if (isEditing) {
 return (
 <div className={cn("space-y-1.5", className)}>
 <label className="block sm:text-[11px] text-[10px] text-slate-400 tracking-wider">{label}</label>
 {type === 'select' ? (
 <Select 
 value={value} 
 onValueChange={onChange} 
 options={options || []}
 className="w-full h-9 sm:text-sm text-xs"
 />
 ) : (
 <Input 
 type={type} 
 value={value} 
 onChange={(e) => onChange(e.target.value)}
 className="w-full h-9 sm:text-sm text-xs bg-slate-50/50 border-slate-100 focus:bg-white"
 />
 )}
 </div>
 );
 }

 return (
 <div className={cn("space-y-1", className)}>
 <p className="sm:text-sm text-xs text-slate-400 tracking-wider">{label}</p>
 {isMasked && !isEditing ? (
 <AuthMask>
 <p className="sm:text-sm text-xs text-slate-700 leading-relaxed">{value ||"-"}</p>
 </AuthMask>
 ) : (
 <p className="sm:text-sm text-xs text-slate-700 leading-relaxed">{value ||"-"}</p>
 )}
 </div>
 );
}
